/**
 * @krisspy-file
 * @type page
 * @name "CancellationEmails"
 * @title "Cancellation Emails"
 * @description "Configure cancellation email templates"
 * @routes ["/cancellation-emails"]
 * @flowName "communication"
 * @layout "DashboardLayout"
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  getCancellationEmailByType,
  getExistingCancellationEmailLanguages,
  createCancellationEmail,
  updateCancellationEmail,
  CancellationEmail,
  CancellationEmailInsert
} from '../services/cancellationEmails.service';
import { prepareHtmlForQuill, prepareHtmlForStorage } from '../utils/htmlConversion';

// Quill toolbar configuration
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'bold', 'italic', 'underline',
  'list', 'bullet',
  'align',
  'link'
];

// Email types for cancellation emails
const EMAIL_TYPES = [
  { type: 1, label: 'Cancellation Confirmation', description: 'Sent when a booking is cancelled' },
  { type: 2, label: 'Refund Notification', description: 'Sent when a refund is processed' }
];

export default function CancellationEmails() {
  const { t } = useTranslation();
  const { merchantId } = useAuth();
  const navigate = useNavigate();
  const { defaultLanguage, availableLanguages } = useLanguage();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [activeType, setActiveType] = useState(1);

  // Track which languages already have templates in the DB
  const [existingLanguages, setExistingLanguages] = useState<string[]>([]);

  // Form data
  const [emailData, setEmailData] = useState<Partial<CancellationEmail> | null>(null);
  const [formData, setFormData] = useState({
    subject: '',
    email_text: '',
    hi_hello: ''
  });

  useEffect(() => {
    if (merchantId) {
      loadEmailData();
      loadExistingLanguages();
    }
  }, [merchantId, selectedLanguage, activeType]);

  async function loadExistingLanguages() {
    if (!merchantId) return;
    const { data } = await getExistingCancellationEmailLanguages(merchantId);
    if (data) setExistingLanguages(data);
  }

  async function loadEmailData() {
    if (!merchantId) return;

    setLoading(true);
    const { data, error } = await getCancellationEmailByType(merchantId, activeType, selectedLanguage);

    if (error) {
      console.error('Error loading cancellation email:', error);
    }

    if (data) {
      setEmailData(data);
      setFormData({
        subject: data.subject || '',
        email_text: prepareHtmlForQuill(data.email_text),
        hi_hello: data.hi_hello || ''
      });
    } else {
      setEmailData(null);
      setFormData({
        subject: '',
        email_text: '',
        hi_hello: ''
      });
    }

    setLoading(false);
  }

  async function handleSave() {
    if (!merchantId) return;

    setSaving(true);

    const dataToSave: CancellationEmailInsert = {
      merchant_id: merchantId,
      language: selectedLanguage,
      type: activeType,
      subject: formData.subject,
      email_text: prepareHtmlForStorage(formData.email_text),
      hi_hello: formData.hi_hello || null
    };

    let result;
    if (emailData?.id) {
      result = await updateCancellationEmail(emailData.id, dataToSave);
    } else {
      result = await createCancellationEmail(dataToSave);
    }

    if (result.error) {
      showToast(t('cancellationEmails.errorSaving') + result.error, 'error');
    } else {
      showToast(t('cancellationEmails.savedSuccess'), 'success');
      if (result.data) {
        setEmailData(result.data);
      }
      // Refresh existing languages after creating/updating a template
      loadExistingLanguages();
    }

    setSaving(false);
  }

  if (loading && !emailData) {
    return (
      <div className="container-centered">
        <Loader2 className="w-8 h-8 animate-spin text-blue" />
        <p className="mt-md text-secondary">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      <div className="flex items-center gap-md mb-xl">
        <button onClick={() => navigate('/dashboard')} className="btn btn-ghost p-xs">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="heading-page">{t('cancellationEmails.title')}</h1>
          <p className="text-sm text-secondary">{t('cancellationEmails.subtitle')}</p>
        </div>
      </div>

      <div className="space-y-lg">
        {/* Language Selector */}
        {availableLanguages.length > 0 && (
          <div className="card mb-lg">
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">{t('cancellationEmails.emailLanguage')}</label>
                <p className="text-sm text-secondary mb-sm">
                  {t('cancellationEmails.languageDesc')}
                </p>
                <div className="flex gap-sm mt-sm overflow-x-auto">
                  {availableLanguages.map((lang) => {
                    const hasTemplate = existingLanguages.includes(lang.code);
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setSelectedLanguage(lang.code)}
                        className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                          selectedLanguage === lang.code
                            ? 'bg-blue text-white'
                            : 'bg-secondary text-secondary hover:bg-primary/10'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                        {lang.code === defaultLanguage && (
                          <span className="text-xs opacity-75 ml-xs">{t('common.default')}</span>
                        )}
                        {!hasTemplate && (
                          <span className={`text-xs px-xs py-px rounded ${
                            selectedLanguage === lang.code
                              ? 'bg-white/20 text-white'
                              : 'bg-warning/20 text-warning'
                          }`}>{t('common.new')}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Type Tabs */}
        <div className="card mb-lg">
          <div className="card-body">
            <div className="flex gap-sm overflow-x-auto">
              {EMAIL_TYPES.map((emailType) => (
                <button
                  key={emailType.type}
                  onClick={() => setActiveType(emailType.type)}
                  className={`btn whitespace-nowrap ${activeType === emailType.type ? 'btn-primary' : 'btn-ghost'}`}
                >
                  {emailType.type === 1 ? t('cancellationEmails.cancellationConfirmation') : t('cancellationEmails.refundNotification')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Email Form */}
        <div className="card">
          <div className="card-header">
            <h2 className="heading-section">
              {activeType === 1 ? t('cancellationEmails.cancellationConfirmation') : t('cancellationEmails.refundNotification')}
            </h2>
            <p className="text-sm text-secondary mt-xs">
              {activeType === 1 ? t('cancellationEmails.cancellationDesc') : t('cancellationEmails.refundDesc')}
            </p>
          </div>
          <div className="card-body space-y-lg">
            {/* Subject */}
            <div className="form-group">
              <label className="form-label">{t('cancellationEmails.subject')}</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="input w-full"
                placeholder={t('cancellationEmails.subjectPlaceholder')}
              />
            </div>

            {/* Hi/Hello Greeting */}
            <div className="form-group">
              <label className="form-label">{t('cancellationEmails.greeting')}</label>
              <input
                type="text"
                value={formData.hi_hello}
                onChange={(e) => setFormData({ ...formData, hi_hello: e.target.value })}
                className="input w-full"
                placeholder={t('cancellationEmails.greetingPlaceholder')}
              />
              <p className="text-xs text-secondary mt-xs">
                {t('cancellationEmails.greetingHint')}
              </p>
            </div>

            {/* Email Body */}
            <div className="form-group">
              <label className="form-label">{t('cancellationEmails.emailContent')}</label>
              <div className="quill-wrapper">
                <ReactQuill
                  theme="snow"
                  value={formData.email_text}
                  onChange={(value) => setFormData({ ...formData, email_text: value })}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder={t('cancellationEmails.contentPlaceholder')}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-lg flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {t('cancellationEmails.saveCancellationEmail')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
